import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { adminAPI, serviceRequestsAPI, consultationsAPI } from '@/lib/api';
import { Globe, GraduationCap, Briefcase, FileText, LogOut, Settings, MessageSquare, Image, Calendar, Type, Award, School, Mail, Clock, Users, BarChart3 } from 'lucide-react';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recentRequests, setRecentRequests] = useState([]);
  const [recentConsultations, setRecentConsultations] = useState([]);

  useEffect(() => {
    const verifyAndFetch = async () => {
      try {
        await adminAPI.verify();
        const [statsRes, requestsRes, consultationsRes] = await Promise.all([
          adminAPI.getStats(),
          serviceRequestsAPI.getAll(),
          consultationsAPI.getAll(),
        ]);
        setStats(statsRes.data);
        setRecentRequests(requestsRes.data.slice(0, 5)); // Get 5 most recent
        setRecentConsultations(consultationsRes.data.slice(0, 5));
      } catch (error) {
        navigate('/admin/login');
      } finally {
        setLoading(false);
      }
    };
    verifyAndFetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount - navigate is stable from useNavigate

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    navigate('/admin/login');
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><div className="text-[#D4AF37]">در حال بارگذاری...</div></div>;
  }

  return (
    <div className="min-h-screen bg-[#121212] py-8" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-light text-white">پنل مدیریت</h1>
          <button
            onClick={handleLogout}
            data-testid="logout-button"
            className="flex items-center gap-2 px-4 py-2 border border-white/20 text-white rounded-lg hover:bg-white/5 transition-colors"
          >
            خروج
            <LogOut size={18} />
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="glass-effect border border-white/10 rounded-xl p-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-zinc-400 text-sm mb-1">کشورها</div>
                <div className="text-3xl font-semibold text-white">{stats?.countries || 0}</div>
              </div>
              <Globe className="text-[#D4AF37]" size={32} />
            </div>
          </div>
          <div className="glass-effect border border-white/10 rounded-xl p-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-zinc-400 text-sm mb-1">دانشگاه‌ها</div>
                <div className="text-3xl font-semibold text-white">{stats?.universities || 0}</div>
              </div>
              <GraduationCap className="text-[#D4AF37]" size={32} />
            </div>
          </div>
          <div className="glass-effect border border-white/10 rounded-xl p-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-zinc-400 text-sm mb-1">فرصت‌های شغلی</div>
                <div className="text-3xl font-semibold text-white">{stats?.jobs || 0}</div>
              </div>
              <Briefcase className="text-[#D4AF37]" size={32} />
            </div>
          </div>
          <div className="glass-effect border border-white/10 rounded-xl p-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-zinc-400 text-sm mb-1">درخواست‌ها</div>
                <div className="text-3xl font-semibold text-white">{stats?.consultations || 0}</div>
              </div>
              <FileText className="text-[#D4AF37]" size={32} />
            </div>
          </div>
        </div>

        {/* Quick Links */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <Link
            to="/admin/analytics"
            className="glass-effect border border-[#D4AF37]/30 rounded-xl p-6 hover:border-[#D4AF37]/50 transition-all bg-gradient-to-br from-[#D4AF37]/10 to-transparent"
          >
            <div className="flex items-center gap-2 mb-2">
              <h3 className="text-xl font-medium text-[#D4AF37]">📊 آمار بازدید سایت</h3>
              <BarChart3 className="text-[#D4AF37]" size={20} />
            </div>
            <p className="text-sm text-zinc-300">مشاهده آمار و تحلیل بازدیدکنندگان</p>
          </Link>
          <Link
            to="/admin/countries"
            data-testid="manage-countries-link"
            className="glass-effect border border-white/10 rounded-xl p-6 hover:border-[#D4AF37]/30 transition-all"
          >
            <h3 className="text-xl font-medium text-white mb-2">مدیریت کشورها</h3>
            <p className="text-sm text-zinc-400">افزودن، ویرایش و حذف کشورها</p>
          </Link>
          <Link
            to="/admin/countries-management"
            className="glass-effect border border-white/10 rounded-xl p-6 hover:border-[#D4AF37]/30 transition-all"
          >
            <h3 className="text-xl font-medium text-white mb-2">هزینه‌ها و رشته‌ها</h3>
            <p className="text-sm text-zinc-400">ویرایش هزینه زندگی و رشته‌های کشورها</p>
          </Link>
          <Link
            to="/admin/universities"
            data-testid="manage-universities-link"
            className="glass-effect border border-white/10 rounded-xl p-6 hover:border-[#D4AF37]/30 transition-all"
          >
            <h3 className="text-xl font-medium text-white mb-2">مدیریت دانشگاه‌ها</h3>
            <p className="text-sm text-zinc-400">افزودن، ویرایش و حذف دانشگاه‌ها</p>
          </Link>
          <Link
            to="/admin/universities-management"
            className="glass-effect border border-white/10 rounded-xl p-6 hover:border-[#D4AF37]/30 transition-all"
          >
            <h3 className="text-xl font-medium text-white mb-2">🎓 ویرایش جامع دانشگاه‌ها</h3>
            <p className="text-sm text-zinc-400">رشته‌ها، امکانات، شرایط پذیرش و...</p>
          </Link>
          <Link
            to="/admin/schools"
            data-testid="manage-schools-link"
            className="glass-effect border border-white/10 rounded-xl p-6 hover:border-[#D4AF37]/30 transition-all"
          >
            <div className="flex items-center gap-2 mb-2">
              <h3 className="text-xl font-medium text-white">مدیریت مدرسه‌ها</h3>
              <School className="text-[#D4AF37]" size={20} />
            </div>
            <p className="text-sm text-zinc-400">افزودن، ویرایش و حذف مدرسه‌ها</p>
          </Link>
          <Link
            to="/admin/schools-management"
            className="glass-effect border border-white/10 rounded-xl p-6 hover:border-[#D4AF37]/30 transition-all"
          >
            <h3 className="text-xl font-medium text-white mb-2">🏫 ویرایش جامع مدرسه‌ها</h3>
            <p className="text-sm text-zinc-400">رشته‌ها، امکانات، شرایط پذیرش و...</p>
          </Link>
          <Link
            to="/admin/jobs"
            data-testid="manage-jobs-link"
            className="glass-effect border border-white/10 rounded-xl p-6 hover:border-[#D4AF37]/30 transition-all"
          >
            <h3 className="text-xl font-medium text-white mb-2">مدیریت فرصت‌ها</h3>
            <p className="text-sm text-zinc-400">افزودن، ویرایش و حذف فرصت‌های شغلی</p>
          </Link>
          <Link
            to="/admin/articles"
            data-testid="manage-articles-link"
            className="glass-effect border border-white/10 rounded-xl p-6 hover:border-[#D4AF37]/30 transition-all"
          >
            <h3 className="text-xl font-medium text-white mb-2">مدیریت مقالات</h3>
            <p className="text-sm text-zinc-400">افزودن، ویرایش و حذف مقالات</p>
          </Link>
          <Link
            to="/admin/testimonials"
            data-testid="manage-testimonials-link"
            className="glass-effect border border-white/10 rounded-xl p-6 hover:border-[#D4AF37]/30 transition-all"
          >
            <div className="flex items-center gap-2 mb-2">
              <h3 className="text-xl font-medium text-white">مدیریت نظرات</h3>
              <MessageSquare className="text-[#D4AF37]" size={20} />
            </div>
            <p className="text-sm text-zinc-400">افزودن، ویرایش و حذف نظرات مشتریان</p>
          </Link>
          <Link
            to="/admin/gallery"
            data-testid="manage-gallery-link"
            className="glass-effect border border-white/10 rounded-xl p-6 hover:border-[#D4AF37]/30 transition-all"
          >
            <div className="flex items-center gap-2 mb-2">
              <h3 className="text-xl font-medium text-white">گالری سمینارها</h3>
              <Image className="text-[#D4AF37]" size={20} />
            </div>
            <p className="text-sm text-zinc-400">مدیریت تصاویر سمینارها و رویدادها</p>
          </Link>
          <Link
            to="/admin/events"
            data-testid="manage-events-link"
            className="glass-effect border border-white/10 rounded-xl p-6 hover:border-[#D4AF37]/30 transition-all"
          >
            <div className="flex items-center gap-2 mb-2">
              <h3 className="text-xl font-medium text-white">مدیریت رویدادها</h3>
              <Calendar className="text-[#D4AF37]" size={20} />
            </div>
            <p className="text-sm text-zinc-400">افزودن و مدیریت رویدادهای آینده</p>
          </Link>
          <Link
            to="/admin/content"
            data-testid="manage-content-link"
            className="glass-effect border border-white/10 rounded-xl p-6 hover:border-[#D4AF37]/30 transition-all"
          >
            <div className="flex items-center gap-2 mb-2">
              <h3 className="text-xl font-medium text-white">مدیریت محتوا</h3>
              <Type className="text-[#D4AF37]" size={20} />
            </div>
            <p className="text-sm text-zinc-400">ویرایش متن‌های صفحات سایت</p>
          </Link>
          <Link
            to="/admin/partners"
            data-testid="manage-partners-link"
            className="glass-effect border border-white/10 rounded-xl p-6 hover:border-[#D4AF37]/30 transition-all"
          >
            <div className="flex items-center gap-2 mb-2">
              <h3 className="text-xl font-medium text-white">مدیریت لوگوهای شرکا</h3>
              <Award className="text-[#D4AF37]" size={20} />
            </div>
            <p className="text-sm text-zinc-400">افزودن و مدیریت لوگوهای دانشگاه‌ها</p>
          </Link>
          <Link
            to="/admin/consultations"
            data-testid="manage-consultations-link"
            className="glass-effect border border-white/10 rounded-xl p-6 hover:border-[#D4AF37]/30 transition-all"
          >
            <h3 className="text-xl font-medium text-white mb-2">درخواست‌ها</h3>
            <p className="text-sm text-zinc-400">مشاهده درخواست‌های مشاوره</p>
          </Link>
          <Link
            to="/admin/settings"
            data-testid="manage-settings-link"
            className="glass-effect border border-white/10 rounded-xl p-6 hover:border-[#D4AF37]/30 transition-all"
          >
            <div className="flex items-center gap-2 mb-2">
              <h3 className="text-xl font-medium text-white">تنظیمات سایت</h3>
              <Settings className="text-[#D4AF37]" size={20} />
            </div>
            <p className="text-sm text-zinc-400">مدیریت لوگو، اطلاعات تماس و شبکه‌های اجتماعی</p>
          </Link>
          <Link
            to="/admin/admins"
            data-testid="manage-admins-link"
            className="glass-effect border border-white/10 rounded-xl p-6 hover:border-[#D4AF37]/30 transition-all"
          >
            <div className="flex items-center gap-2 mb-2">
              <h3 className="text-xl font-medium text-white">مدیریت ادمین‌ها</h3>
              <Users className="text-[#D4AF37]" size={20} />
            </div>
            <p className="text-sm text-zinc-400">افزودن، حذف و تغییر رمز ادمین‌ها</p>
          </Link>
          <Link
            to="/"
            className="glass-effect border border-white/10 rounded-xl p-6 hover:border-[#D4AF37]/30 transition-all"
          >
            <h3 className="text-xl font-medium text-white mb-2">بازگشت به سایت</h3>
            <p className="text-sm text-zinc-400">مشاهده سایت اصلی</p>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;